import React, { useRef, useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '@/components/ui/button';
import { Eraser, Check } from 'lucide-react';

export const SignaturePad = forwardRef(function SignaturePad({ onChange, value, height = 220 }, ref) {
  const sigRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  // Nota: `getTrimmedCanvas()` di react-signature-canvas dipende da `trim-canvas`
  // che soffre di un noto bug di interop ESM con Webpack 5 ("trim_canvas... is not a function").
  // Bypassiamo il problema usando direttamente `getCanvas()` (l'area è gi\u00e0 delimitata dal box).
  const canvasToDataUrl = () => {
    const inst = sigRef.current;
    if (!inst) return '';
    try { if (inst.isEmpty && inst.isEmpty()) return ''; } catch { /* ignore */ }
    try {
      if (typeof inst.getCanvas === 'function') {
        return inst.getCanvas().toDataURL('image/png');
      }
      if (typeof inst.toDataURL === 'function') {
        return inst.toDataURL('image/png');
      }
    } catch (e) { /* ignore */ }
    return '';
  };

  useImperativeHandle(ref, () => ({
    clear: () => { sigRef.current?.clear(); setIsEmpty(true); onChange?.(''); },
    getDataUrl: () => canvasToDataUrl(),
    isEmpty: () => sigRef.current?.isEmpty?.() ?? true,
  }));

  useEffect(() => {
    if (value && sigRef.current && sigRef.current.isEmpty()) {
      try {
        sigRef.current.fromDataURL(value);
        setIsEmpty(false);
      } catch (e) { /* ignore */ }
    }
  }, [value]);

  const handleEnd = () => {
    if (sigRef.current) {
      const empty = sigRef.current.isEmpty();
      setIsEmpty(empty);
      if (!empty) {
        const url = canvasToDataUrl();
        if (url) onChange?.(url);
      }
    }
  };

  const handleClear = () => {
    sigRef.current?.clear();
    setIsEmpty(true);
    onChange?.('');
  };

  return (
    <div className="space-y-3">
      <div className="signature-canvas-wrap" style={{ height }} data-testid="signature-pad-canvas">
        <SignatureCanvas
          ref={sigRef}
          penColor="#0F3D2E"
          canvasProps={{ className: 'w-full h-full' }}
          onEnd={handleEnd}
        />
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">Firma con il dito, mouse o pennino. Tocca &laquo;Pulisci&raquo; per riprovare.</p>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={handleClear} data-testid="signature-pad-clear-button">
            <Eraser className="size-4 mr-1" /> Pulisci
          </Button>
          <Button type="button" size="sm" disabled={isEmpty} data-testid="signature-pad-confirm-button" onClick={handleEnd}>
            <Check className="size-4 mr-1" /> Conferma
          </Button>
        </div>
      </div>
    </div>
  );
});
